import { useMemo, useState } from "react"

import { usePostAccountPasswordChange } from "@boundless/bl-api-client/allauth/account"
import { PostAccountPasswordChangeBody } from "@boundless/bl-api-client/allauth/allauth.zod"
import { getCommonUserRetrieveQueryKey } from "@boundless/bl-api-client/common/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { CheckCircle2Icon, LoaderIcon, LockIcon } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import { getAuthClient } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"

// Extends the generated API body with frontend-only concerns: a confirm field,
// the match check, and (in change mode) requiring the current password before
// submit. The API itself leaves current_password optional.
function makePasswordSchema(hasPassword: boolean) {
  return PostAccountPasswordChangeBody.extend({
    confirm_password: z.string().min(1, "Please confirm your password"),
  }).superRefine((data, ctx) => {
    if (data.new_password !== data.confirm_password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirm_password"],
        message: "Passwords don't match",
      })
    }
    if (hasPassword && !data.current_password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["current_password"],
        message: "Enter your current password",
      })
    }
  })
}

type PasswordFormValues = z.infer<ReturnType<typeof makePasswordSchema>>

/**
 * Add or change the account password from My Account.
 *
 * A code-only account starts with no usable password, so `hasPassword` drives
 * the mode: "Add" (New + Confirm, no current password) vs "Change" (current
 * password required). Both hit the same allauth change-password endpoint, which
 * treats a missing current password as a set-password.
 */
export function PasswordDialog({ hasPassword }: { hasPassword: boolean }) {
  const client = getAuthClient()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const schema = useMemo(() => makePasswordSchema(hasPassword), [hasPassword])

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  })

  const {
    mutate: savePassword,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = usePostAccountPasswordChange({
    mutation: {
      onSuccess: () => {
        form.reset()
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error)
      },
    },
  })

  const onSubmit = (data: PasswordFormValues) => {
    savePassword({
      client,
      data: hasPassword
        ? {
            current_password: data.current_password,
            new_password: data.new_password,
          }
        : { new_password: data.new_password },
    })
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      // Refresh the account on close so the row reflects the new state without
      // flipping the dialog's own copy while its success message is still up.
      if (isSuccess) {
        queryClient.invalidateQueries({
          queryKey: getCommonUserRetrieveQueryKey(),
        })
      }
      form.reset()
      resetMutation()
    }
  }

  const ctaLabel = hasPassword ? "Change Password" : "Add password"
  const successMessage = hasPassword
    ? "Password changed successfully! You can now use your new password to sign in."
    : "Password added! You can now sign in with your password or an email code."

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-40">
          <LockIcon className="mr-2 h-4 w-4" />
          {ctaLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {hasPassword ? "Change Password" : "Add a password"}
          </DialogTitle>
          <DialogDescription>
            {hasPassword
              ? "Enter your current password and choose a new one."
              : "Choose a password so you can sign in with it as well as an email code."}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2Icon className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {successMessage}
            </AlertDescription>
          </Alert>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {hasPassword && (
                <FormInput
                  name="current_password"
                  placeholder="Current Password"
                  type="password"
                />
              )}
              <div className="space-y-2">
                <FormInput
                  name="new_password"
                  placeholder="New Password"
                  type="password"
                />
                <FormInput
                  name="confirm_password"
                  placeholder="Confirm New Password"
                  type="password"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    ctaLabel
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
